import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaCamera, FaDownload, FaTimes } from 'react-icons/fa';

const ScreenShot = ({ color }) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Take a screenshot of the screen
  const takeSimpleScreenshot = useCallback(async () => {
    try {
      setIsCapturing(true);
      console.log("Requesting screen capture...");
      
      // Request screen capture permission
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { 
          cursor: "always",
          // Try application capture to avoid the entire monitor
          displaySurface: "window" 
        }
      });
      
      // Get the video track from the stream
      const videoTrack = stream.getVideoTracks()[0];
      
      // Create a video element to load the stream
      const video = document.createElement('video');
      video.srcObject = stream;
      
      // Wait for the video to load metadata and play one frame
      await new Promise(resolve => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });
      
      // Give it a moment to render the first frame, but don't wait too long
      // as the browser sharing indicator might appear
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Create a canvas to draw the video frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw the current video frame to the canvas
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Get the image data as a PNG
      const screenshotUrl = canvas.toDataURL('image/png');
      
      // Stop all tracks immediately to minimize the chance of capturing the indicator
      stream.getTracks().forEach(track => track.stop());
      
      // Update state to show the preview
      setScreenshotPreview(screenshotUrl);
      setShowPreview(true);
      setIsCapturing(false);
      
      console.log("Screenshot captured successfully");
      
    } catch (error) {
      console.error("Error capturing screenshot:", error);
      
      if (error.name === 'NotAllowedError') {
        alert('Permission denied. Please allow screen access to capture screenshots.');
      } else {
        alert(`Error capturing screenshot: ${error.message}`);
      }
      
      setIsCapturing(false);
    }
  }, []);
  
  // Download the screenshot
  const downloadScreenshot = useCallback(() => {
    if (!screenshotPreview) return;
    
    try {
      // Create a download link
      const a = document.createElement('a');
      a.style.display = 'none';
      
      // Format filename with date/time
      const date = new Date();
      const formattedDate = date.toISOString().split('T')[0];
      const formattedTime = date.toTimeString().split(' ')[0].replace(/:/g, '-');
      
      a.href = screenshotPreview;
      a.download = `screenshot-${formattedDate}-${formattedTime}.png`;
      
      // Add to DOM, click and remove
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
      }, 100);
      
      console.log("Screenshot downloaded");
    } catch (error) {
      console.error("Error downloading screenshot:", error);
      alert(`Error downloading screenshot: ${error.message}`);
    }
  }, [screenshotPreview]);
  
  // Close the preview
  const closePreview = useCallback(() => {
    setShowPreview(false);
    // Optional: Also clear the screenshot data if no longer needed
    // setScreenshotPreview(null);
  }, []);
  
  return (
    <div className="relative font-sans">
      {/* Screenshot button - removed "Capturing..." text */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={takeSimpleScreenshot}
        disabled={isCapturing}
        className="flex items-center justify-center p-2 rounded-full text-white transition-all duration-200"
        title="Take Screenshot"
        style={{ color: color || '#2888b5' }}
      >
        <FaCamera size={18} />
      </motion.button>
      
      {/* Screenshot preview panel */}
      {showPreview && screenshotPreview && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl max-h-full flex flex-col overflow-hidden"
          >
            {/* Preview header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-b">
              <h3 className="font-semibold text-gray-800">Screenshot Preview</h3>
              <button 
                onClick={closePreview}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <FaTimes size={18} />
              </button>
            </div>
            
            {/* Preview image container */}
            <div className="flex-grow overflow-auto p-4">
              <img 
                src={screenshotPreview} 
                alt="Screenshot Preview" 
                className="max-w-full h-auto rounded border border-gray-200"
              />
            </div>
            
            {/* Actions footer */}
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={closePreview}
                className="px-4 py-2 mr-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
              <button
                onClick={downloadScreenshot}
                className="flex items-center px-4 py-2 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              >
                <FaDownload className="mr-2" /> Download
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ScreenShot;